// conversationAdapter/elicitation — the pure elicitation-form projection
// helpers, split out of `conversationAdapter.ts` (module-topology decomposition;
// verbatim). The ONE place a serialized/folded elicitation `request` (or one
// answer) becomes its UI mirror. Shared by BOTH the DURABLE transcript projection
// (`toChatEntries`, on a full reload) and the TRANSIENT turn-stream codec
// (`toTurnStreamEvent`, the same form arriving live mid-turn), so the two produce
// byte-identical entries that dedup by id. Pure + total: no I/O, no throw.

export { toChatAnswer, toInformationRequestEntry };

import type {
    ChatChoiceOption,
    ChatField,
    ChatFieldAnswer,
    ChatScaleLevel,
    ChatStep,
    InformationRequestEntry,
    InformationRequestStatus,
} from "../../components/Chat/types.js";
import {
    FALLBACK_FIELD_KEY,
    OWN_ANSWER_FIELD_KEY,
} from "../../components/Chat/types.js";
import { fields, list, num, str } from "../wireShape.js";

// Omit `note` entirely when absent so the entry's optional field is truly
// optional (absent ≠ empty string).
const noteOf = (note: string | undefined): { readonly note?: string } =>
    note === undefined ? {} : { note };

// The always-answerable fallback field for a request whose own fields couldn't
// be projected (never-nothing). A single free-text input the Author can reply
// in; the agent's prose above the card carries the actual question. Keyed by the
// reserved `FALLBACK_FIELD_KEY` so `InformationRequestCard` renders a LOCALIZED
// label + placeholder at the edge (F5) — the raw strings here are inert (the card
// overrides them by key), present only so a non-localizing reader still sees
// something. Mirrors the app-side fallback (epilogue.ts), which uses the same key.
const fallbackChatField = (): ChatField => ({
    type: "field_text",
    key: FALLBACK_FIELD_KEY,
    label: "Your answer",
    placeholder: "Type your answer",
});

const fallbackChatStep = (): ChatStep => ({
    key: "step",
    fields: [fallbackChatField()],
});

// The always-present FREE-TEXT ESCAPE field. The agent's choice options are
// SUGGESTIONS, never a closed set — so the author is never locked into them.
// Keyed by the reserved `OWN_ANSWER_FIELD_KEY` so the card renders a LOCALIZED
// label + placeholder at the edge (the raw strings here are inert).
const ownAnswerChatField = (): ChatField => ({
    type: "field_text",
    key: OWN_ANSWER_FIELD_KEY,
    label: "Or write your own answer",
    placeholder: "Type your own answer",
});

const isChoiceField = (field: ChatField): boolean =>
    field.type === "field_single_choice" || field.type === "field_multi_choice";

// Guarantee the free-text escape on a step (F53/free-text valve): a step that
// offers CHOICE questions but carries NO free-text field of its own gets one
// appended, so every set of suggestions has an always-visible "write your own"
// companion. A step the agent already gave a `field_text` (the doctrine escape)
// is left untouched — one escape is enough. Pure and total.
const withOwnAnswerEscape = (step: ChatStep): ChatStep => {
    const hasChoice = step.fields.some(isChoiceField);
    const hasText = step.fields.some((f) => f.type === "field_text");
    if (!hasChoice || hasText) return step;
    return { ...step, fields: [...step.fields, ownAnswerChatField()] };
};

// Build one `InformationRequestEntry` from a folded/serialized `request` map.
// The ONE place a request map becomes a UI form entry — shared by the DURABLE
// projection (from the conversation's artifact accumulator, on a full reload) AND
// the TRANSIENT turn-stream codec (F3, the same form arriving LIVE mid-turn), so
// the live form and the reloaded one are byte-identical and dedup by id. A
// request with no title is dropped (undefined). Steps are never empty — a request
// whose fields all fail to project still yields one fallback step (never-nothing).
const toInformationRequestEntry = (
    id: string,
    request: ReadonlyMap<string, unknown> | undefined,
    status: InformationRequestStatus,
): InformationRequestEntry | undefined => {
    const title = str(request?.get("title"));
    if (title === undefined) return undefined;
    return {
        type: "information_request",
        id,
        title,
        ...noteOf(str(request?.get("note"))),
        steps: projectRequestSteps(request),
        status,
    };
};

// Project one domain `ElicitationStep` into its UI `ChatStep`. Total — a step
// whose fields all drop yields undefined (the step is skipped).
const toChatStep = (raw: unknown): ChatStep | undefined => {
    const s = fields(raw);
    const stepFields = list(s?.get("fields"))
        .map(toChatField)
        .filter((f): f is ChatField => f !== undefined);
    if (stepFields.length === 0) return undefined;
    const title = str(s?.get("title"));
    const note = str(s?.get("note"));
    return {
        key: str(s?.get("key")) ?? "step",
        ...(title === undefined ? {} : { title }),
        ...(note === undefined ? {} : { note }),
        fields: stepFields,
    };
};

// Wrap a legacy flat `fields` request as a single anonymous step.
const legacyFlatStep = (
    request: ReadonlyMap<string, unknown> | undefined,
): ChatStep | undefined => {
    const stepFields = list(request?.get("fields"))
        .map(toChatField)
        .filter((f): f is ChatField => f !== undefined);
    return stepFields.length === 0
        ? undefined
        : { key: "step", fields: stepFields };
};

// Project a request into its UI steps — never empty. A `steps` request maps each
// step (dropping empty ones); a legacy flat `fields` request wraps as one step;
// a request whose fields all fail to project still yields ONE fallback step, so
// the agent's question is always answerable (never-nothing).
const projectRequestSteps = (
    request: ReadonlyMap<string, unknown> | undefined,
): readonly ChatStep[] => {
    const rawSteps = list(request?.get("steps"));
    const steps = (
        rawSteps.length > 0
            ? rawSteps.map(toChatStep)
            : [legacyFlatStep(request)]
    ).filter((s): s is ChatStep => s !== undefined);
    // Every step's choice options are SUGGESTIONS — guarantee a free-text escape
    // beside them so the author is never locked into fixed choices (F53).
    return steps.length > 0
        ? steps.map(withOwnAnswerEscape)
        : [fallbackChatStep()];
};

// Project one folded domain `Field` into its UI `ChatField` mirror. Total —
// a missing key/label or an unknown `type` yields undefined (the field is
// dropped). Mirrors the domain `Elicitation.Field` sum one-for-one.
const toChatField = (raw: unknown): ChatField | undefined => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    const key = str(f?.get("key"));
    const label = str(f?.get("label"));
    if (key === undefined || label === undefined) return undefined;
    const help = str(f?.get("help"));
    const helpPart = help === undefined ? {} : { help };
    switch (type) {
        case "field_text": {
            const placeholder = str(f?.get("placeholder"));
            return {
                type: "field_text",
                key,
                label,
                ...helpPart,
                ...(placeholder === undefined ? {} : { placeholder }),
            };
        }
        case "field_single_choice":
            return {
                type: "field_single_choice",
                key,
                label,
                ...helpPart,
                options: choiceOptionsOf(f?.get("options")),
            };
        case "field_multi_choice":
            return {
                type: "field_multi_choice",
                key,
                label,
                ...helpPart,
                options: choiceOptionsOf(f?.get("options")),
            };
        case "field_scale": {
            const min = num(f?.get("min"));
            const max = num(f?.get("max"));
            if (min === undefined || max === undefined) return undefined;
            const minLabel = str(f?.get("minLabel"));
            const maxLabel = str(f?.get("maxLabel"));
            const levels = scaleLevelsOf(f?.get("levels"));
            return {
                type: "field_scale",
                key,
                label,
                ...helpPart,
                min,
                max,
                ...(minLabel === undefined ? {} : { minLabel }),
                ...(maxLabel === undefined ? {} : { maxLabel }),
                ...(levels.length === max - min + 1 ? { levels } : {}),
            };
        }
        case "field_sources":
            return { type: "field_sources", key, label, ...helpPart };
        default:
            return undefined;
    }
};

// A scale's levels in words; an unreadable level is dropped (and a list
// that no longer covers the range is not used).
const scaleLevelsOf = (raw: unknown): readonly ChatScaleLevel[] =>
    list(raw).flatMap((item) => {
        const f = fields(item);
        const label = str(f?.get("label"));
        if (label === undefined) return [];
        const meaning = str(f?.get("meaning"));
        return [meaning === undefined ? { label } : { label, meaning }];
    });

const choiceOptionsOf = (raw: unknown): readonly ChatChoiceOption[] =>
    list(raw)
        .map((o) => {
            const opt = fields(o);
            const value = str(opt?.get("value"));
            const label = str(opt?.get("label"));
            if (value === undefined || label === undefined) return undefined;
            const help = str(opt?.get("help"));
            return help === undefined
                ? { value, label }
                : { value, label, help };
        })
        .filter((o): o is ChatChoiceOption => o !== undefined);

// Project one folded domain `FieldAnswer` into its UI `ChatFieldAnswer` mirror.
// Total — unknown type / missing value yields undefined (the answer is dropped).
const toChatAnswer = (raw: unknown): ChatFieldAnswer | undefined => {
    const a = fields(raw);
    const type = str(a?.get("type"));
    const key = str(a?.get("key"));
    if (key === undefined) return undefined;
    switch (type) {
        case "answer_text": {
            const value = str(a?.get("value"));
            return value === undefined
                ? undefined
                : { type: "answer_text", key, value };
        }
        case "answer_single_choice": {
            const value = str(a?.get("value"));
            return value === undefined
                ? undefined
                : { type: "answer_single_choice", key, value };
        }
        case "answer_multi_choice": {
            const values = list(a?.get("values"))
                .map((v) => str(v))
                .filter((v): v is string => v !== undefined);
            return { type: "answer_multi_choice", key, values };
        }
        case "answer_scale": {
            const value = num(a?.get("value"));
            return value === undefined
                ? undefined
                : { type: "answer_scale", key, value };
        }
        case "answer_sources": {
            const sources = list(a?.get("sources")).flatMap((raw) => {
                const s = fields(raw);
                const id = str(s?.get("sourceId"));
                const title = str(s?.get("title"));
                return id === undefined || title === undefined
                    ? []
                    : [{ id, title }];
            });
            return { type: "answer_sources", key, sources };
        }
        default:
            return undefined;
    }
};
